import React, { useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  Plus, Pencil, Trash2, X, Check, BookOpen,
  Layers, Calendar, Infinity, Tag, Settings
} from 'lucide-react';
import api from '../../api/client';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { useAuth } from '../../context/AuthContext';

// ── Tutorial Assign Modal ─────────────────────────────
function TutorialAssignModal({ batch, onClose, onSaved }) {
  const [tutorials, setTutorials]   = useState([]);
  const [selected, setSelected]     = useState([]);
  const [loading, setLoading]       = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [allTuts, batchTuts] = await Promise.all([
          api.get('/tutorials'),
          api.get(`/batches/${batch.id}/tutorials`),
        ]);
        setTutorials(allTuts.data.data.filter(t => !t.disable));
        setSelected(batchTuts.data.data.map(t => t.id));
      } catch { toast.error('Failed to load'); }
      finally { setLoading(false); }
    };
    load();
  }, [batch.id]);

  const toggleTutorial = (id) =>
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const handleSave = async () => {
    try {
      await api.post(`/batches/${batch.id}/tutorials`, { tutorial_ids: selected });
      toast.success('Tutorials assigned');
      onSaved();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <span className="modal-title">
            <BookOpen size={18} /> Assign Tutorials — {batch.batchName}
          </span>
          <button className="btn-icon" onClick={onClose}><X size={18} /></button>
        </div>
        <div className="modal-body">
          <div className="alert alert-info" style={{ marginBottom: 16, fontSize: 12 }}>
            Select which tutorial volumes students in this batch will have access to.
          </div>
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <div style={{
              display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))',
              gap: 10, maxHeight: 400, overflowY: 'auto'
            }}>
              {tutorials.map(t => (
                <label key={t.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer',
                  background: selected.includes(t.id) ? 'var(--orange-50)' : 'var(--white)',
                  border: `1.5px solid ${selected.includes(t.id) ? 'var(--orange-400)' : 'var(--grey-200)'}`,
                  borderRadius: 8, padding: '10px 12px',
                }}>
                  <input type="checkbox" checked={selected.includes(t.id)}
                    onChange={() => toggleTutorial(t.id)} style={{ marginTop: 3 }} />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--grey-800)' }}>{t.name}</div>
                    <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                      {t.key_value && (
                        <code style={{ fontSize: 10, background: 'var(--orange-100)', padding: '1px 6px', borderRadius: 3, color: 'var(--orange-600)' }}>
                          {t.key_value}
                        </code>
                      )}
                      {Number(t.cost) > 0
                        ? <span style={{ fontSize: 10, color: 'var(--grey-400)' }}>₹{t.cost}</span>
                        : <span style={{ fontSize: 10, color: 'var(--green-600)' }}>Free</span>}
                    </div>
                  </div>
                </label>
              ))}
              {!tutorials.length && <p style={{ color: 'var(--grey-400)', fontSize: 13 }}>No tutorials found</p>}
            </div>
          )}
          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--grey-400)' }}>
            {selected.length} tutorial{selected.length !== 1 ? 's' : ''} selected
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave}><Check size={14} /> Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Main Batches Page ─────────────────────────────────
export default function BatchesPage() {
  const { can } = useAuth();
  const [batches, setBatches]       = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [modal, setModal]           = useState(false);   // 'form' | 'tutorials'
  const [editing, setEditing]       = useState(null);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [confirm, setConfirm]       = useState(null);
  const [yearFilter, setYearFilter] = useState('');
  const [showDisabled, setShowDisabled] = useState(false);

  const { register, handleSubmit, reset, watch, setValue, formState: { errors } } = useForm();
  const isPerennial = watch('is_perennial');

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (yearFilter) { params.set('academic_year_id', yearFilter); params.set('include_perennial', 'true'); }
      if (showDisabled) params.set('includeDisabled', 'true');

      const [b, ay] = await Promise.all([
        api.get(`/batches?${params}`),
        api.get('/masters/academic-years'),
      ]);
      setBatches(b.data.data);
      setAcademicYears(ay.data.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [yearFilter, showDisabled]);

  useEffect(() => { load(); }, [load]);

  const activeYear = academicYears.find(y => y.is_active);

  const openCreate = () => {
    setEditing(null);
    reset({
      batchName: '', is_perennial: false,
      academic_year_id: activeYear?.id || '', disable: false
    });
    setModal('form');
  };

  const openEdit = (batch) => {
    setEditing(batch);
    reset({
      batchName: batch.batchName,
      is_perennial: !!batch.is_perennial,
      academic_year_id: batch.academic_year_id || '',
      disable: !!batch.disable,
    });
    setModal('form');
  };

  const onSubmit = async (values) => {
    try {
      const payload = {
        ...values,
        is_perennial: !!values.is_perennial,
        academic_year_id: values.is_perennial ? null : (values.academic_year_id || null),
      };
      if (editing) {
        await api.put(`/batches/${editing.id}`, payload);
        toast.success('Batch updated');
      } else {
        await api.post('/batches', payload);
        toast.success('Batch created');
      }
      setModal(false); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Error'); }
  };

  const doDelete = async (id) => {
    try { await api.delete(`/batches/${id}`); toast.success('Deleted'); setConfirm(null); load(); }
    catch (err) { toast.error(err.response?.data?.message || 'Error'); setConfirm(null); }
  };

  const getBadge = (batch) => {
    if (batch.is_perennial) {
      return <span className="badge badge-blue" style={{ fontSize: 10 }}><Infinity size={10} /> Perennial</span>;
    }
    if (batch.year_label) {
      return <span className="badge badge-orange" style={{ fontSize: 10 }}><Calendar size={10} /> {batch.year_label}</span>;
    }
    return <span className="badge badge-grey" style={{ fontSize: 10 }}>No year set</span>;
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Batches</h1>
          <p className="page-subtitle">Manage batches, assign tutorials, generate student codes</p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Year filter */}
          <select className="form-control" style={{ width: 180 }}
            value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
            <option value="">All Years + Perennial</option>
            {academicYears.map(ay => (
              <option key={ay.id} value={ay.id}>
                {ay.year_label}{ay.is_active ? ' (Active)' : ''}
              </option>
            ))}
          </select>
          <label className="toggle">
            <input type="checkbox" checked={showDisabled} onChange={e => setShowDisabled(e.target.checked)} />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            <span style={{ fontSize: 13 }}>Show disabled</span>
          </label>
          {can('batches','create') && (
            <button className="btn btn-primary" onClick={openCreate}><Plus size={15} /> New Batch</button>
          )}
        </div>
      </div>

      {/* Info bar */}
      <div className="alert alert-info" style={{ marginBottom: 20, fontSize: 12 }}>
        <Infinity size={14} />
        <span><strong>Perennial batches</strong> run regardless of academic year (e.g. online users, private learners).
        Year-tagged batches are linked to a specific academic year for school programmes.</span>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title"><Layers size={15} style={{ color: 'var(--orange-400)' }} /> {batches.length} batches</span>
          {yearFilter && (
            <span className="badge badge-orange">
              Showing: {academicYears.find(y => y.id === Number(yearFilter))?.year_label} + Perennial
            </span>
          )}
        </div>
        <div className="table-wrap">
          {loading ? <div className="loader-wrap"><div className="loader" /></div> : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Batch Name</th>
                  <th>Code</th>
                  <th>Year / Type</th>
                  <th>Tutorials</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id} style={{ opacity: b.disable ? 0.55 : 1 }}>
                    <td style={{ fontWeight: 600 }}>{b.batchName}</td>
                    <td>
                      <code style={{
                        fontFamily: 'monospace', fontSize: 12,
                        background: 'var(--grey-100)', padding: '2px 8px',
                        borderRadius: 4, color: 'var(--grey-700)', letterSpacing: '.04em'
                      }}>
                        {b.batchCode}
                      </code>
                    </td>
                    <td>{getBadge(b)}</td>
                    <td>
                      <span className="badge badge-blue">{b.tutorial_count ?? 0} tutorials</span>
                    </td>
                    <td>
                      {b.disable
                        ? <span className="badge badge-red">Disabled</span>
                        : <span className="badge badge-green">Active</span>}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--grey-400)' }}>
                      {new Date(b.date).toLocaleDateString('en-IN')}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4 }}>
                        {can('batches','edit') && (
                          <button className="btn btn-sm btn-secondary" title="Assign Tutorials"
                            onClick={() => { setSelectedBatch(b); setModal('tutorials'); }}>
                            <BookOpen size={12} /> Tutorials
                          </button>
                        )}
                        {can('batches','edit') && (
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(b)}>
                            <Pencil size={14} />
                          </button>
                        )}
                        {can('batches','delete') && (
                          <button className="btn-icon" title="Delete" onClick={() => setConfirm(b.id)}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!batches.length && (
                  <tr><td colSpan={7}>
                    <div className="empty-state"><Layers size={36} /><h3>No batches found</h3></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ─── Create/Edit Modal ───────────────────────── */}
      {modal === 'form' && (
        <div className="modal-overlay" onClick={() => setModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">{editing ? 'Edit' : 'Create'} Batch</span>
              <button className="btn-icon" onClick={() => setModal(false)}><X size={18} /></button>
            </div>
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Batch Name <span className="req">*</span></label>
                  <input className={`form-control ${errors.batchName ? 'error' : ''}`}
                    placeholder="e.g. Jr KG 2025-26"
                    {...register('batchName', { required: 'Required' })} />
                  {errors.batchName && <p className="form-error">{errors.batchName.message}</p>}
                </div>

                {editing && (
                  <div className="form-group">
                    <label className="form-label">Batch Code</label>
                    <div style={{
                      fontFamily: 'monospace', fontSize: 16, fontWeight: 700,
                      letterSpacing: '.12em', color: 'var(--orange-600)',
                      background: 'var(--orange-50)', padding: '8px 14px',
                      borderRadius: 6, border: '1px solid var(--orange-200)'
                    }}>
                      {editing.batchCode}
                    </div>
                    <p style={{ fontSize: 11, color: 'var(--grey-400)', marginTop: 4 }}>
                      Auto-generated. Cannot be changed after creation.
                    </p>
                  </div>
                )}

                <div style={{ background: 'var(--orange-50)', border: '1px solid var(--orange-200)', borderRadius: 8, padding: '14px 16px', marginBottom: 16 }}>
                  <label className="toggle" style={{ marginBottom: 10 }}>
                    <input type="checkbox" {...register('is_perennial')} />
                    <span className="toggle-track"><span className="toggle-thumb" /></span>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>
                      <Infinity size={14} style={{ display: 'inline', marginRight: 4 }} />
                      Perennial Batch (independent of academic year)
                    </span>
                  </label>
                  <p style={{ fontSize: 11, color: 'var(--grey-500)', marginTop: 4 }}>
                    Use this for online users, private learners, or any batch that runs continuously regardless of school year.
                  </p>
                </div>

                {!isPerennial && (
                  <div className="form-group">
                    <label className="form-label">Academic Year <span className="req">*</span></label>
                    <select className={`form-control ${errors.academic_year_id ? 'error' : ''}`}
                      {...register('academic_year_id', {
                        validate: v => isPerennial || !!v || 'Required when not perennial'
                      })}>
                      <option value="">Select academic year…</option>
                      {academicYears.map(ay => (
                        <option key={ay.id} value={ay.id}>
                          {ay.year_label}{ay.is_active ? ' ✓ Active' : ''}
                        </option>
                      ))}
                    </select>
                    {errors.academic_year_id && <p className="form-error">{errors.academic_year_id.message}</p>}
                  </div>
                )}

                {editing && (
                  <label className="toggle">
                    <input type="checkbox" {...register('disable')} />
                    <span className="toggle-track"><span className="toggle-thumb" /></span>
                    <span style={{ fontSize: 13 }}>Disabled</span>
                  </label>
                )}

                {!editing && (
                  <div className="alert alert-info" style={{ marginTop: 12, fontSize: 12 }}>
                    A unique 5-character batch code will be automatically generated on creation.
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary"><Check size={14} /> Save Batch</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ─── Tutorial Assign Modal ───────────────────── */}
      {modal === 'tutorials' && selectedBatch && (
        <TutorialAssignModal
          batch={selectedBatch}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); load(); }}
        />
      )}

      <ConfirmDialog open={!!confirm} title="Delete Batch"
        message="This will permanently remove the batch. Batches with orders cannot be deleted."
        onConfirm={() => doDelete(confirm)} onCancel={() => setConfirm(null)} />
    </div>
  );
}
